// Deep import: the @thoughtspot/admin entry pulls the admin UI into this chunk.
import { readSeatUsage } from '@thoughtspot/admin/lib/src/utils/user-limit.util';
import { UserSeatUsage } from '@thoughtspot/session-service';
import { useCallback } from 'react';
import { useApolloClient } from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    GetSeatUsageUsersDocument,
    GetSeatUsageUsersQuery,
    useGetGroupMembersLazyQuery as _useGetGroupMembersLazyQuery,
    useGetUserAndGroupListLazyQuery as _useGetUserAndGroupListLazyQuery,
    useGetUserAndGroupListQuery as _useGetUserAndGroupsListQuery,
    useGetUserDetailsByIdLazyQuery as _useGetUserDetailsByIdLazyQuery,
    useGetUserGroupsListQuery as _useGetUserGroupsListQuery,
    useGetUsersListQuery as _useGetUsersListQuery,
} from '../generated/graphql-types';

export const useGetUsersListQuery = (
    pattern?: string,
    skipIds?: Array<string>,
) => {
    const { data, error, loading } = _useGetUsersListQuery({
        variables: {
            pattern,
            skipIds,
        },
    });

    return {
        error,
        loading,
        data: data?.getUsersList,
    };
};

export const useGetUserGroupListQuery = (
    pattern?: string,
    skipIds?: Array<string>,
) => {
    const { data, error, loading } = _useGetUserGroupsListQuery({
        variables: {
            pattern,
            skipIds,
        },
    });

    return {
        error,
        loading,
        data: data?.getUserGroupsList,
    };
};

export const useGetUserAndGroupListQuery = (
    pattern?: string,
    skipIds?: Array<string>,
) => {
    const { data, error, loading, refetch } = _useGetUserAndGroupsListQuery({
        variables: {
            pattern,
            skipIds,
        },
    });

    return {
        error,
        loading,
        data,
        refetch,
    };
};

export const useGetUserAndGroupListLazyQuery = () => {
    const [
        getUserGroupsList,
        { data, loading, error },
    ] = _useGetUserAndGroupListLazyQuery();

    return {
        getUserGroupsList,
        data,
        loading,
        error,
    };
};

export const useGetGroupMembersLazyQuery = () => {
    const [
        getGroupMembers,
        { loading, data, error },
    ] = _useGetGroupMembersLazyQuery();

    return {
        getGroupMembers,
        error,
        loading,
        data: data?.getGroupMembers,
    };
};

export const useGetUserDetailsByIdLazyQuery = (
    ids: string[],
    orgOverride?: string,
) => {
    const [
        getUserById,
        { data, error, loading },
    ] = _useGetUserDetailsByIdLazyQuery({
        variables: {
            ids,
            orgOverride,
        },
    });

    return {
        error,
        loading,
        data,
        getUserById,
    };
};

// Seat usage of the given orgs (all orgs when omitted). The admin app has a
// copy built on its own generated types.
export const useFetchSeatUsage = () => {
    const client = useApolloClient();

    const fetchSeatUsage = useCallback(
        // Uncached: blink isn't told when users change elsewhere.
        (orgIdentifiers?: string[]): Promise<UserSeatUsage | null> =>
            readSeatUsage(orgIdentifiers, async variables => {
                const { data } = await client.query<GetSeatUsageUsersQuery>({
                    query: GetSeatUsageUsersDocument,
                    variables,
                    fetchPolicy: 'no-cache',
                });
                return data?.Users__searchUsers?.data;
            }),
        [client],
    );

    return { fetchSeatUsage };
};
